/**
 *  @class
 *  @function ImageTextBanner
 *
 *  Custom element for the "Image with text banner" section.
 *  Handles the optional discount-activator block (AJAX discount code activation,
 *  cookie/cart sync, active-state UI). No animation / parallax dependencies.
 */

if (!customElements.get('image-with-text-banner')) {
  class ImageTextBanner extends HTMLElement {
    constructor() {
      super();

      this.discountStateTimers = [];
      this.discountVerificationPromise = null;
      this.productDiscountStateReady = false;
      this.stateAnimationTimers = new WeakMap();
      this.boundDiscountClick = this.onDiscountActivatorClick.bind(this);
      this.boundDiscountChange = this.onDiscountChange.bind(this);
      this.boundPageShow = this.onPageShow.bind(this);
      this.boundCodeLink = this.onCodeLink.bind(this);
      this.boundCartDiscountChange = this.onCartDiscountChange.bind(this);
    }

    connectedCallback() {
      if (!this.querySelector('.iwto-discount-activator')) {
        return;
      }

      this.initializeDiscountActivator();
      document.addEventListener('iwto:discount:changed', this.boundDiscountChange);
      window.addEventListener('pageshow', this.boundPageShow);
      window.addEventListener('co2.codelink', this.boundCodeLink);
      window.addEventListener('co2.cart.initialized', this.boundCartDiscountChange);
      window.addEventListener('co2.afterCartAttributeChange', this.boundCartDiscountChange);
      document.addEventListener('cart:updated', this.boundCartDiscountChange);
    }

    disconnectedCallback() {
      document.removeEventListener('iwto:discount:changed', this.boundDiscountChange);
      window.removeEventListener('pageshow', this.boundPageShow);
      window.removeEventListener('co2.codelink', this.boundCodeLink);
      window.removeEventListener('co2.cart.initialized', this.boundCartDiscountChange);
      window.removeEventListener('co2.afterCartAttributeChange', this.boundCartDiscountChange);
      document.removeEventListener('cart:updated', this.boundCartDiscountChange);
      this.clearDiscountStateTimers();
      this.querySelectorAll('.iwto-discount-activator__button').forEach((button) => {
        button.removeEventListener('click', this.boundDiscountClick);
      });
    }

    initializeDiscountActivator() {
      this.querySelectorAll('.iwto-discount-activator__button').forEach((button) => {
        button.removeEventListener('click', this.boundDiscountClick);
        button.addEventListener('click', this.boundDiscountClick);
      });

      this.syncDiscountActivatorState(true);
      if (this.querySelector('[data-iwto-layout="product"]')) {
        this.verifyPersistedDiscountState();
      }
    }

    onDiscountChange(event) {
      if (event.detail?.code) {
        this.updateDiscountActivatorState(this.getActiveDiscountCodes() || event.detail.code);
        this.queueDiscountStateChecks([400, 900, 1500]);
        this.scheduleDiscountVerification([700, 1500]);
        return;
      }

      this.syncDiscountActivatorState(true);
    }

    onPageShow() {
      this.syncDiscountActivatorState(true);
    }

    onCodeLink() {
      this.syncDiscountActivatorState();
    }

    onCartDiscountChange() {
      this.verifyPersistedDiscountState();
    }

    async onDiscountActivatorClick(event) {
      const button = event.currentTarget;
      const discountCode = button.dataset.iwtoDiscountCode?.trim();

      if (!discountCode || button.disabled || button.closest('.iwto-discount-activator')?.classList.contains('is-loading')) {
        return;
      }

      const activator = button.closest('.iwto-discount-activator');
      const isProductLayout = activator?.dataset.iwtoLayout === 'product';
      if (isProductLayout && this.getActiveDiscountCodes()) {
        this.updateDiscountActivatorState(this.getActiveDiscountCodes());
        return;
      }
      this.setDiscountFeedback(activator, '', true);
      activator?.classList.remove('has-error');
      activator?.classList.add('is-loading');

      try {
        if (isProductLayout && typeof co2 !== 'undefined' && typeof co2.getDiscountCode === 'function') {
          const activeCode = await co2.getDiscountCode();
          if (activeCode) {
            this.updateDiscountActivatorState(activeCode);
            return;
          }
        }
        const normalizedCode = discountCode.toUpperCase();
        await this.applyDiscountCode(normalizedCode);

        this.setCookie('discount_code', normalizedCode, 30);
        this.setStoredDiscountCode(normalizedCode);
        this.refreshDiscountDisplays();
        this.notifyDiscountIntegrations(normalizedCode);
        document.dispatchEvent(
          new CustomEvent('iwto:discount:changed', {
            detail: { code: normalizedCode }
          })
        );
      } catch (error) {
        console.error('Error applying image-with-text-banner discount code:', error);
        activator?.classList.add('has-error');
        this.setDiscountFeedback(activator, 'Code konnte nicht aktiviert werden.', false);
      } finally {
        activator?.classList.remove('is-loading');
        this.syncDiscountActivatorState();
      }
    }

    async applyDiscountCode(discountCode) {
      if (typeof co2 !== 'undefined' && typeof co2.setDiscountCode === 'function') {
        const applied = await co2.setDiscountCode(discountCode);

        if (!applied) {
          throw new Error(`Failed to apply Datora discount code: ${discountCode}`);
        }

        return;
      }

      const response = await fetch(`/discount/${encodeURIComponent(discountCode)}`, {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error(`Failed to apply discount code: ${discountCode}`);
      }
    }

    refreshDiscountDisplays() {
      if (typeof window.updateDatoraDiscounts === 'function') {
        window.updateDatoraDiscounts();
      }
    }

    notifyDiscountIntegrations(discountCode) {
      if (typeof co2 !== 'undefined' && typeof co2.getDiscountCode === 'function') {
        window.dispatchEvent(
          new CustomEvent('co2.codelink', {
            detail: { code: discountCode, source: 'image-with-text-banner' }
          })
        );
      }

      document.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { open: false, source: 'image-with-text-banner', code: discountCode }
        })
      );
    }

    syncDiscountActivatorState(syncFromCart = false) {
      this.updateDiscountActivatorState(this.getActiveDiscountCodes());
      this.queueDiscountStateChecks();

      if (syncFromCart) {
        this.queueDiscountStateChecks([400, 900, 1500]);
        this.scheduleDiscountVerification([700, 1500]);
      }
    }

    queueDiscountStateChecks(delays = [250, 900]) {
      this.clearDiscountStateTimers();

      delays.forEach((delay) => {
        const timerId = window.setTimeout(() => {
          this.updateDiscountActivatorState(this.getActiveDiscountCodes());
        }, delay);

        this.discountStateTimers.push(timerId);
      });
    }

    clearDiscountStateTimers() {
      this.discountStateTimers.forEach((timerId) => {
        window.clearTimeout(timerId);
      });
      this.discountStateTimers = [];
    }

    scheduleDiscountVerification(delays = [700]) {
      delays.forEach((delay) => {
        const timerId = window.setTimeout(() => {
          this.verifyPersistedDiscountState();
        }, delay);

        this.discountStateTimers.push(timerId);
      });
    }

    updateDiscountActivatorState(activeDiscountCodes) {
      const codes = (activeDiscountCodes || '')
        .split(',')
        .map((code) => code.trim().toUpperCase())
        .filter(Boolean);

      this.querySelectorAll('.iwto-discount-activator__button').forEach((button) => {
        const activator = button.closest('.iwto-discount-activator');
        const label = activator?.querySelector('.iwto-discount-activator__label');
        const buttonText = button.querySelector('.iwto-discount-activator__button-text');
        const icon = button.querySelector('.iwto-discount-activator__icon');
        const code = button.dataset.iwtoDiscountCode?.trim().toUpperCase();
        const isActive = Boolean(code) && codes.includes(code);
        const isProductLayout = activator?.dataset.iwtoLayout === 'product';
        const isAnyActive = codes.length > 0;
        if (isProductLayout) {
          this.hidden = !this.productDiscountStateReady || isAnyActive;
        }
        const nextState = isAnyActive ? 'active' : 'inactive';
        const previousState = activator?.dataset.iwtoState;
        const defaultActivatorLabel = activator?.dataset.iwtoLabelDefault || '';
        const activeActivatorLabel = activator?.dataset.iwtoLabelActive !== undefined ? activator.dataset.iwtoLabelActive : defaultActivatorLabel;
        const defaultLabel = button.dataset.iwtoDiscountDefaultLabel || code;

        if (activator && previousState && previousState !== nextState) {
          this.animateActivatorStateChange(activator);
        }

        button.disabled = isAnyActive;
        button.setAttribute('aria-pressed', isAnyActive ? 'true' : 'false');

        if (label && !isProductLayout) {
          label.textContent = isAnyActive ? activeActivatorLabel : defaultActivatorLabel;
          label.hidden = isAnyActive && activeActivatorLabel === '';
        }

        if (buttonText) {
          buttonText.textContent = isAnyActive ? (isProductLayout ? activeActivatorLabel : codes[0]) : defaultLabel;
          buttonText.hidden = isAnyActive && activeActivatorLabel === '';
        }

        if (icon) {
          icon.hidden = isAnyActive;
        }

        if (activator) {
          activator.classList.toggle('is-active', isAnyActive);
          activator.dataset.iwtoState = nextState;
          if (isAnyActive && activeActivatorLabel === '') {
            activator.style.display = 'none';
          } else {
            activator.style.display = '';
          }
        }

        if (isActive) {
          activator?.classList.remove('has-error');
          this.setDiscountFeedback(activator, '', true);
        }
      });
    }

    setDiscountFeedback(activator, message, hidden) {
      const feedback = activator?.querySelector('.iwto-discount-activator__feedback');

      if (!feedback) {
        return;
      }

      feedback.textContent = message;
      feedback.hidden = hidden;
    }

    animateActivatorStateChange(activator) {
      const existingTimer = this.stateAnimationTimers.get(activator);
      if (existingTimer) {
        window.clearTimeout(existingTimer);
      }

      activator.classList.add('is-updating');

      const timerId = window.setTimeout(() => {
        activator.classList.remove('is-updating');
        this.stateAnimationTimers.delete(activator);
      }, 240);

      this.stateAnimationTimers.set(activator, timerId);
    }

    async verifyPersistedDiscountState() {
      if (this.discountVerificationPromise) {
        return this.discountVerificationPromise;
      }

      this.discountVerificationPromise = (async () => {
        const confirmedCodes = await this.getConfirmedDiscountCodes();
        this.productDiscountStateReady = true;

        if (confirmedCodes) {
          const normalizedCodes = this.normalizeCodes(confirmedCodes);

          this.setCookie('discount_code', normalizedCodes.join(','), 30);
          this.setStoredDiscountCode(normalizedCodes[0] || null);
          this.updateDiscountActivatorState(normalizedCodes.join(','));
          return normalizedCodes.join(',');
        }

        this.clearDiscountPersistence();
        this.updateDiscountActivatorState('');
        return '';
      })().finally(() => {
        this.discountVerificationPromise = null;
      });

      return this.discountVerificationPromise;
    }

    async getConfirmedDiscountCodes() {
      const co2Codes = await this.getDiscountCodesFromCo2();
      if (co2Codes.length) {
        return co2Codes.join(',');
      }

      const cartCodes = await this.getDiscountCodesFromCart();
      if (cartCodes.length) {
        return cartCodes.join(',');
      }

      return '';
    }

    async getDiscountCodesFromCo2() {
      if (typeof co2 === 'undefined' || typeof co2.getDiscountCode !== 'function') {
        return [];
      }

      try {
        const currentCode = await co2.getDiscountCode();
        const normalizedCode = this.normalizeCode(currentCode);
        return normalizedCode ? [normalizedCode] : [];
      } catch (error) {
        return [];
      }
    }

    async getDiscountCodesFromCart() {
      try {
        const response = await fetch('/cart.js', {
          method: 'GET',
          credentials: 'same-origin',
          cache: 'no-store'
        });

        if (!response.ok) {
          return [];
        }

        const cart = await response.json();
        const discountCodes = Array.isArray(cart?.discount_codes) ? cart.discount_codes : [];

        return discountCodes
          .map((discount) => this.normalizeCode(discount?.code || discount))
          .filter(Boolean);
      } catch (error) {
        return [];
      }
    }

    getActiveDiscountCodes() {
      const cookieCodes = this.normalizeCodes(this.getCookie('discount_code'));
      const storedCode = this.normalizeCode(this.getStoredDiscountCode());

      if (!storedCode) {
        return cookieCodes.join(',');
      }

      if (cookieCodes.includes(storedCode)) {
        return cookieCodes.join(',');
      }

      return [...cookieCodes, storedCode].filter(Boolean).join(',');
    }

    normalizeCode(code) {
      return code?.trim().toUpperCase() || '';
    }

    normalizeCodes(codes) {
      return (codes || '')
        .split(',')
        .map((code) => this.normalizeCode(code))
        .filter(Boolean);
    }

    getStoredDiscountCode() {
      try {
        return sessionStorage.getItem('lastDiscountCode');
      } catch (error) {
        return null;
      }
    }

    setStoredDiscountCode(code) {
      try {
        if (code) {
          sessionStorage.setItem('lastDiscountCode', this.normalizeCode(code));
        } else {
          sessionStorage.removeItem('lastDiscountCode');
        }
      } catch (error) {
        // Ignore storage failures in restricted browsers.
      }
    }

    clearDiscountPersistence() {
      this.setCookie('discount_code', '', -1);
      this.setStoredDiscountCode(null);
    }

    getCookie(name) {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);

      if (parts.length === 2) {
        const cookieValue = parts.pop().split(';').shift();
        return cookieValue && cookieValue !== 'null' && cookieValue !== 'undefined'
          ? cookieValue
          : null;
      }

      return null;
    }

    setCookie(name, value, days) {
      let expires = '';

      if (days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = `; expires=${date.toUTCString()}`;
      }

      document.cookie = `${name}=${value || ''}${expires}; path=/`;
    }
  }

  customElements.define('image-with-text-banner', ImageTextBanner);
}
